import { CommonModule } from '@angular/common';
import { CdkDragDrop, DragDropModule, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, OnInit } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatCardModule } from '@angular/material/card';
import { MatDialog } from '@angular/material/dialog';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { firstValueFrom } from 'rxjs';
import { AppGeneralStateService } from 'src/app/app-general-state.service';
import { DialogConfirmDeleteComponent } from 'src/app/core/dialog/dialog-confirm-parker-delete/dialog-confirm-delete.component';
import { ParcsDataService } from 'src/app/core/services/data/parcs-data.service';
import { MessageDialogService } from 'src/app/core/services/helper/message-dialog.service';
import { IFlashPool } from 'src/app/shared/code/interfaces/parcs-interfaces';

@Component({
  selector: 'app-flash-pools-admin',
  standalone: true,
  imports: [
    CommonModule,
    ReactiveFormsModule,
    DragDropModule,
    MatCardModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTooltipModule,
  ],
  templateUrl: './flash-pools-admin.component.html',
  styleUrl: './flash-pools-admin.component.scss',
})
export class FlashPoolsAdminComponent implements OnInit {
  pools: IFlashPool[] = [];
  poolForm: FormGroup;
  isProcessing: boolean = false;

  //Services
  _appGeneralState = inject(AppGeneralStateService);
  _parcsDataService = inject(ParcsDataService);
  _messageDialogService = inject(MessageDialogService);
  _matDialogService = inject(MatDialog);

  ngOnInit(): void {
    this.initPoolForm();
    this.getPoolsForLot();
  }

  initPoolForm(): void {
    this.poolForm = new FormGroup({
      poolName: new FormControl('', [Validators.required, Validators.maxLength(255)]),
    });
  }

  onResetPoolForm(): void {
    this.poolForm.reset({ poolName: '' });
  }

  async getPoolsForLot(): Promise<void> {
    this.isProcessing = true;

    try {
      this.pools = await firstValueFrom(
        this._parcsDataService.flash_GetPoolsByLotNumber(this._appGeneralState.currentLotNumber),
      );
    } catch (err) {
      this.pools = [];
      this._messageDialogService.snackBarTopCenterTimed(
        'The Flash Pools could not be loaded.  Please try again.',
        5000,
      );
    }

    this.isProcessing = false;
  }

  async onSubmitAddPool(): Promise<void> {
    const poolName: string = (this.poolForm.get('poolName')!.value ?? '').trim();

    if (poolName === '') {
      return;
    }

    this.isProcessing = true;

    try {
      await firstValueFrom(this._parcsDataService.flash_AddPool(this._appGeneralState.currentLotNumber, poolName));
      this._messageDialogService.snackBarTopCenterTimed('The Flash Pool has been added.', 5000);
      this.onResetPoolForm();
      await this.getPoolsForLot();
    } catch (err: any) {
      this.isProcessing = false;
      // The API returns the reason as the body on a 400, e.g. a duplicate name.
      this._messageDialogService.snackBarTopCenterTimed(
        typeof err?.error === 'string' && err.error ? err.error : 'The Flash Pool could not be added.',
        5000,
      );
    }
  }

  onDeletePool(poolId: string): void {
    const dialogRef = this._matDialogService.open(DialogConfirmDeleteComponent);

    dialogRef.afterClosed().subscribe(async (result: boolean) => {
      if (result) {
        await this.deletePool(poolId);
      }
    });
  }

  /// Persists the whole order, not just the moved pool - moving one row shifts every row between
  /// its old and new position. A failure reloads from the server so the UI cannot show a fake order.
  async onPoolDropped(event: CdkDragDrop<IFlashPool[]>): Promise<void> {
    if (event.previousIndex === event.currentIndex) {
      return;
    }

    moveItemInArray(this.pools, event.previousIndex, event.currentIndex);

    try {
      await firstValueFrom(
        this._parcsDataService.flash_UpdatePoolSortOrder(
          this._appGeneralState.currentLotNumber,
          this.pools.map((pool) => pool.poolId),
        ),
      );
      this._messageDialogService.snackBarTopCenterTimed('The Pool order has been saved.', 3000);
    } catch (err) {
      this._messageDialogService.snackBarTopCenterTimed('The Pool order could not be saved.', 5000);
      await this.getPoolsForLot();
    }
  }

  /// Removes the name from the spACE-AR list only; accounts already assigned to it in Flash are unchanged.
  async deletePool(poolId: string): Promise<void> {
    this.isProcessing = true;

    try {
      await firstValueFrom(this._parcsDataService.flash_DeletePool(poolId));
      this._messageDialogService.snackBarTopCenterTimed('The Flash Pool has been deleted.', 5000);
      await this.getPoolsForLot();
    } catch (err) {
      this.isProcessing = false;
      this._messageDialogService.snackBarTopCenterTimed('The Flash Pool could not be deleted.', 5000);
    }
  }
}
